import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../config/axios';
import AdvancedImportWizard from './AdvancedImportWizard';
import { RefreshCw, Play, RotateCcw, FileDown, FileWarning } from 'lucide-react';
import './ExcelImport.css';

const statusBadgeClass = (status) => {
  if (status === 'completed') return 'is-badge-success';
  if (status === 'failed') return 'is-badge-danger';
  if (status === 'running') return 'is-badge-info';
  if (status === 'pending') return 'is-badge-warning';
  return '';
};

const summaryText = (job) => {
  if (!job) return '';
  let s = job.summary;
  if (typeof s === 'string') {
    try { s = JSON.parse(s); } catch (_) { return s; }
  }
  if (!s || typeof s !== 'object') return '';
  const parts = [];
  if (s.created != null) parts.push(`Created: ${s.created}`);
  if (s.updated != null && s.updated > 0) parts.push(`Updated: ${s.updated}`);
  if (s.skips != null && s.skips > 0) parts.push(`Skips: ${s.skips}`);
  if (s.errors != null) parts.push(`Errors: ${s.errors}`);
  return parts.join(' \u00b7 ') || '';
};

export default function ImportSection({ brands = [], onRefresh }) {
  const [importType, setImportType] = useState('inventory');
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [rollbackingId, setRollbackingId] = useState(null);
  const [rollbackError, setRollbackError] = useState('');
  const [runNowLoadingId, setRunNowLoadingId] = useState(null);
  const [sortCol, setSortCol] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const jobHistoryRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    setRollbackError('');
    try {
      const res = await axiosInstance.get(`/api/tenant/import/jobs?type=${importType}`);
      setJobs(Array.isArray(res.data.jobs) ? res.data.jobs : []);
    } catch (_) {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [importType]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleImportSuccess = () => { fetchJobs(); if (onRefresh) onRefresh(); };

  const handleRollback = async (jobId) => {
    setRollbackingId(jobId);
    setRollbackError('');
    try {
      await axiosInstance.post(`/api/tenant/import/jobs/${jobId}/rollback`);
      await fetchJobs();
      if (onRefresh) onRefresh();
    } catch (err) {
      setRollbackError(err.response?.data?.error || err.message || 'Rollback failed');
    } finally {
      setRollbackingId(null);
    }
  };

  const handleRunNow = async (jobId) => {
    setRunNowLoadingId(jobId);
    setRollbackError('');
    try {
      await axiosInstance.post(`/api/tenant/import/jobs/${jobId}/run`);
      await fetchJobs();
      if (onRefresh) onRefresh();
    } catch (err) {
      setRollbackError(err.response?.data?.error || err.message || 'Run now failed');
    } finally {
      setRunNowLoadingId(null);
    }
  };

  const downloadErrorsCsv = async (jobId) => {
    try {
      const res = await axiosInstance.get(`/api/tenant/import/jobs/${jobId}/errors.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `errors-${jobId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {}
  };

  const downloadSkipsCsv = async (jobId) => {
    try {
      const res = await axiosInstance.get(`/api/tenant/import/jobs/${jobId}/skips.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `skips-${jobId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {}
  };



  const defaultBrandId = brands && brands[0] ? (brands[0].id != null ? brands[0].id : brands[0].value) : null;

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(true); }
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    let va = a[sortCol] ?? '', vb = b[sortCol] ?? '';
    if (sortCol === 'created_at') { va = new Date(va).getTime() || 0; vb = new Date(vb).getTime() || 0; }
    else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
    return sortAsc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const totalJobs = jobs.length;
  const completedJobs = jobs.filter(j => j.status === 'completed').length;
  const failedJobs = jobs.filter(j => j.status === 'failed').length;

  const hasSkips = (job) => {
    let s = job.summary;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch (_) { return false; } }
    return s && typeof s === 'object' && (s.skips > 0 || (Array.isArray(s.skipDetails) && s.skipDetails.length > 0));
  };

  return (
    <div className="is-section">
      <div className="is-section-header">
        <div>
          <h2 className="is-section-title">Import</h2>
          <p className="is-section-subtitle">Import data from spreadsheets</p>
        </div>
      </div>

      <AdvancedImportWizard
        defaultBrandId={defaultBrandId}
        brands={brands}
        onSuccess={handleImportSuccess}
        onTypeChange={(t) => setImportType(t)}
        onJobCreated={() => jobHistoryRef.current?.scrollIntoView({ behavior: 'smooth' })}
      />



      <div className="is-stats-row">
        <div className="card is-stat-card">
          <span className="is-stat-label">TOTAL JOBS</span>
          <span className="is-stat-value">{totalJobs}</span>
        </div>
        <div className="card is-stat-card">
          <span className="is-stat-label">COMPLETED</span>
          <span className="is-stat-value is-stat-success">{completedJobs}</span>
        </div>
        <div className="card is-stat-card">
          <span className="is-stat-label">FAILED</span>
          <span className="is-stat-value is-stat-danger">{failedJobs}</span>
        </div>
      </div>

      <div className="card is-history-card" ref={jobHistoryRef}>
        <div className="is-history-header">
          <h3 className="is-history-title">Job History</h3>
          <button type="button" className="btn btn-secondary is-refresh-btn" onClick={fetchJobs} disabled={jobsLoading}>
            <RefreshCw size={14} className={jobsLoading ? 'is-spin' : ''} /> {jobsLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {rollbackError && <div className="is-error">{rollbackError}</div>}
        {jobsLoading && jobs.length === 0 ? (
          <p className="is-empty">Loading jobs...</p>
        ) : jobs.length === 0 ? (
          <p className="is-empty">No import jobs yet.</p>
        ) : (
          <div className="is-table-wrap">
            <table className="is-table">
              <thead>
                <tr>
                  <th className="is-sortable-th" onClick={() => toggleSort('id')}>Job ID {sortCol === 'id' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</th>
                  <th className="is-sortable-th" onClick={() => toggleSort('type')}>Type {sortCol === 'type' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</th>
                  <th className="is-sortable-th" onClick={() => toggleSort('status')}>Status {sortCol === 'status' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</th>
                  <th className="is-sortable-th" onClick={() => toggleSort('created_at')}>Created {sortCol === 'created_at' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}</th>
                  <th>Summary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((job) => (
                  <tr key={job.id} className="is-table-row">
                    <td><code className="is-job-id">{job.id ? job.id.slice(0, 8) : ''}</code></td>
                    <td className="is-type-cell">{job.type || ''}</td>
                    <td>
                      <span className={'is-badge ' + statusBadgeClass(job.status)}>
                        {runNowLoadingId === job.id ? 'Running...' : (job.status || '\u2014')}
                      </span>
                    </td>
                    <td className="is-date-cell">{job.created_at || ''}</td>
                    <td className="is-summary-cell">{summaryText(job)}</td>
                    <td>
                      <div className="is-actions">
                        {job.status === 'pending' && (
                          <button type="button" className="is-action-btn is-action-primary" title="Run now" onClick={() => handleRunNow(job.id)} disabled={runNowLoadingId != null}>
                            <Play size={14} />
                          </button>
                        )}
                        {(job.status === 'completed' || job.status === 'failed') && (
                          <button type="button" className="is-action-btn" title="Download errors CSV" onClick={() => downloadErrorsCsv(job.id)}>
                            <FileWarning size={14} />
                          </button>
                        )}
                        {job.status === 'completed' && hasSkips(job) && (
                          <button type="button" className="is-action-btn" title="Download skips CSV" onClick={() => downloadSkipsCsv(job.id)}>
                            <FileDown size={14} />
                          </button>
                        )}
                        {job.status === 'completed' && (
                          <button type="button" className="is-action-btn is-action-warning" title="Rollback" onClick={() => handleRollback(job.id)} disabled={rollbackingId === job.id}>
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}